import { colors } from '@fikasio/styles';
import React, { useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import RBForm from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { Icon } from '@fikasio/react-ui-components';
import TrashIcon from 'components/UI/TrashIcon';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useAddConnectorMutation,
  useCancelConnectorRunMutation,
  useDeleteConnectorMutation,
  useGetConnectorCatalogQuery,
  useGetConnectorRunsQuery,
  useGetConnectorsQuery,
  usePatchConnectorMutation,
  useRunConnectorMutation,
} from 'services/connectors/api';
import { Connector, ConnectorRun, ConnectorType, CreateConnectorBody, GitHubConnectorConfig, StravaConnectorConfig, StravaConnectorMetadata, SubstackConnectorConfig, YouTubeConnectorConfig } from 'services/connectors/types';
import { CONNECTOR_FEATURES, normalizeCatalog } from 'services/connectors/catalog';
import { useGetFeaturesQuery } from 'services/features/api';
import { isFeatureEnabled } from 'services/features/catalog';
import { useGetBillingQuery } from 'services/payments/api';
import { toDateTime } from 'utils/date';
import links from 'utils/links';
import ConnectorLogo from './ConnectorLogo';
import styles from './SettingsConnectors.module.css';

const CONNECTOR_RUN_PRICE_USD = 0.01;

const connectorRunChargeAmount = (billedUnits: number) => (
  (billedUnits * CONNECTOR_RUN_PRICE_USD).toFixed(2)
);

type FormState = {
  athlete: string;
  channelId: string;
  handle: string;
  name: string;
  repository: string;
  type: ConnectorType;
  url: string;
};

type View = 'list' | 'catalog' | 'form' | 'runs';

const EMPTY_FORM: FormState = {
  athlete: '',
  channelId: '',
  handle: '',
  name: '',
  repository: '',
  type: 'YOUTUBE',
  url: '',
};

const getErrorMessage = (result: { error?: unknown }, fallback: string) => {
  if (!('error' in result) || !result.error) {
    return null;
  }
  const error = result.error as { error?: string };
  return error.error || fallback;
};

const buildConfig = (form: FormState): CreateConnectorBody['config'] | null => {
  if (form.type === 'GITHUB') {
    const repository = form.repository.trim();
    return repository ? { repository } : null;
  }
  if (form.type === 'SUBSTACK') {
    const url = form.url.trim();
    return url ? { url } : null;
  }
  if (form.type === 'STRAVA') {
    const athlete = form.athlete.trim();
    if (!athlete) {
      return null;
    }
    if (/^https?:\/\//i.test(athlete) || athlete.toLowerCase().includes('strava.com/')) {
      return { url: athlete };
    }
    return { athleteId: athlete };
  }
  const handle = form.handle.trim();
  const channelId = form.channelId.trim();
  if (handle && channelId) {
    return null;
  }
  if (handle) {
    return { handle };
  }
  if (channelId) {
    return { channelId };
  }
  return null;
};

const githubConfig = (connector: Connector): GitHubConnectorConfig | null => (
  connector.type === 'GITHUB' ? connector.config as GitHubConnectorConfig : null
);

const youtubeConfig = (connector: Connector): YouTubeConnectorConfig | null => (
  connector.type === 'YOUTUBE' ? connector.config as YouTubeConnectorConfig : null
);

const substackConfig = (connector: Connector): SubstackConnectorConfig | null => (
  connector.type === 'SUBSTACK' ? connector.config as SubstackConnectorConfig : null
);

const stravaConfig = (connector: Connector): StravaConnectorConfig | null => (
  connector.type === 'STRAVA' ? connector.config as StravaConnectorConfig : null
);

const githubMetadata = (connector: Connector) => (
  connector.metadata && 'openIssueCount' in connector.metadata ? connector.metadata : null
);

const youtubeMetadata = (connector: Connector) => (
  connector.metadata && 'title' in connector.metadata ? connector.metadata : null
);

const substackMetadata = (connector: Connector) => (
  connector.metadata && 'postCount' in connector.metadata ? connector.metadata : null
);

const stravaMetadata = (connector: Connector): StravaConnectorMetadata | null => (
  connector.metadata && 'ytdRunDistance' in connector.metadata ? connector.metadata : null
);

const stravaAthleteName = (connector: Connector) => {
  const metadata = stravaMetadata(connector);
  if (!metadata) {
    return '';
  }
  const name = [metadata.firstname, metadata.lastname].filter(Boolean).join(' ');
  if (!name || name === connector.name) {
    return '';
  }
  return name;
};

const connectorIdentifier = (connector: Connector) => {
  if (connector.type === 'GITHUB') {
    const config = githubConfig(connector);
    return config ? `${config.owner}/${config.repo}` : '';
  }
  if (connector.type === 'SUBSTACK') {
    return substackConfig(connector)?.subdomain
      ?? substackConfig(connector)?.hostname
      ?? '';
  }
  if (connector.type === 'STRAVA') {
    return stravaConfig(connector)?.athleteId ?? '';
  }
  const config = youtubeConfig(connector);
  return config?.handle || config?.channelId || '';
};

const missingConfigMessage = (type: ConnectorType) => {
  if (type === 'GITHUB') {
    return 'provideGitHubRepository';
  }
  if (type === 'SUBSTACK') {
    return 'provideSubstackUrl';
  }
  if (type === 'STRAVA') {
    return 'provideStravaAthlete';
  }
  return 'provideHandleOrChannelId';
};

const formatKilometers = (meters: number) => {
  const kilometers = meters / 1000;
  return kilometers >= 10 ? kilometers.toFixed(0) : kilometers.toFixed(1);
};

const connectorStatsLabel = (
  connector: Connector,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (connector.type === 'GITHUB') {
    const metadata = githubMetadata(connector);
    if (!metadata) {
      return '';
    }
    return [
      t('connectorOpenIssueCount', { count: metadata.openIssueCount }),
      t('connectorOpenPullRequestCount', { count: metadata.openPullRequestCount }),
      t('connectorStarCount', { count: metadata.stargazerCount }),
    ].join(' · ');
  }
  if (connector.type === 'STRAVA') {
    const metadata = stravaMetadata(connector);
    if (!metadata) {
      return '';
    }
    const parts: string[] = [];
    if (metadata.ytdRunCount > 0) {
      parts.push(t('connectorYtdRun', {
        count: metadata.ytdRunCount,
        distance: formatKilometers(metadata.ytdRunDistance),
      }));
    }
    if (metadata.ytdRideCount > 0) {
      parts.push(t('connectorYtdRide', {
        count: metadata.ytdRideCount,
        distance: formatKilometers(metadata.ytdRideDistance),
      }));
    }
    if (metadata.ytdSwimCount > 0) {
      parts.push(t('connectorYtdSwim', {
        count: metadata.ytdSwimCount,
        distance: formatKilometers(metadata.ytdSwimDistance),
      }));
    }
    if (metadata.followerCount != null) {
      parts.push(t('connectorFollowerCount', { count: metadata.followerCount }));
    }
    return parts.join(' · ');
  }
  if (connector.type !== 'SUBSTACK') {
    return '';
  }
  const metadata = substackMetadata(connector);
  if (!metadata) {
    return '';
  }
  const parts: string[] = [];
  parts.push(t('connectorPostCount', { count: metadata.postCount }));
  if (metadata.subscriberCount != null) {
    parts.push(t('connectorSubscriberCount', { count: metadata.subscriberCount }));
  } else if (metadata.subscriberCountLabel) {
    parts.push(metadata.subscriberCountLabel);
  }
  return parts.join(' · ');
};

const matchesSearch = (values: Array<unknown>, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return values.some(value => (
    typeof value === 'string' && value.toLowerCase().includes(normalized)
  ));
};

const formatRunTime = (value?: string) => {
  const relative = toDateTime(value)?.toRelative();
  return relative || value || '';
};

const formatDurationMs = (durationMs: number) => (
  durationMs < 1000
    ? `${durationMs} ms`
    : `${(durationMs / 1000).toFixed(1)} s`
);

const SettingsConnectors = () => {
  const { t } = useTranslation();
  const { data: billing, isLoading: isBillingLoading } = useGetBillingQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: catalog } = useGetConnectorCatalogQuery();
  const {
    data: connectors,
    error: connectorsError,
    isError: connectorsFailed,
  } = useGetConnectorsQuery();
  const { data: features, isError: featuresFailed } = useGetFeaturesQuery();
  const [addConnector, { isLoading: isCreating }] = useAddConnectorMutation();
  const [patchConnector, { isLoading: isPatching }] = usePatchConnectorMutation();
  const [deleteConnector, { isLoading: isDeleting }] = useDeleteConnectorMutation();
  const [runConnector, { isLoading: isRunning }] = useRunConnectorMutation();
  const [cancelConnectorRun, { isLoading: isCancelling }] = useCancelConnectorRunMutation();

  const [view, setView] = useState<View>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [runsConnectorId, setRunsConnectorId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: runs, isLoading: isRunsLoading } = useGetConnectorRunsQuery(runsConnectorId ?? '', {
    skip: !runsConnectorId,
    refetchOnMountOrArgChange: true,
    pollingInterval: runsConnectorId ? 60000 : 0,
  });

  const connectorsLoadError = connectorsFailed
    ? getErrorMessage({ error: connectorsError }, t('failedToLoadConnectors'))
    : null;
  const isBusy = isCreating || isPatching || isDeleting || isRunning;
  const hasBillingSetup = billing?.hasBillingSetup === true;
  const featureFlags = featuresFailed ? undefined : features;
  const connectorsList = (connectors ?? []).filter(connector => (
    isFeatureEnabled(featureFlags, CONNECTOR_FEATURES[connector.type])
  ));
  const runsList = runs ?? [];
  const runsConnector = connectorsList.find(connector => connector.id === runsConnectorId) ?? null;
  const availableCatalog = useMemo(
    () => normalizeCatalog(catalog).filter(item => (
      isFeatureEnabled(featureFlags, CONNECTOR_FEATURES[item.type])
    )),
    [catalog, featureFlags],
  );

  const filteredCatalog = useMemo(() => availableCatalog.filter(item => matchesSearch([
    item.type,
    item.label,
    item.description,
  ], searchQuery)), [availableCatalog, searchQuery]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setView('list');
    setEditingId(null);
    setRunsConnectorId(null);
    setErrorMessage(null);
    setSearchQuery('');
  };

  const startCreate = () => {
    if (!hasBillingSetup) {
      return;
    }
    setForm(EMPTY_FORM);
    setView('catalog');
    setEditingId(null);
    setErrorMessage(null);
    setSearchQuery('');
  };

  const startCreateType = (type: ConnectorType) => {
    setForm({
      ...EMPTY_FORM,
      type,
    });
    setView('form');
    setEditingId(null);
    setErrorMessage(null);
  };

  const cancelForm = () => {
    if (editingId) {
      resetForm();
      return;
    }
    setForm(EMPTY_FORM);
    setView('catalog');
    setErrorMessage(null);
  };

  const startEdit = (connector: Connector) => {
    const github = githubConfig(connector);
    const config = youtubeConfig(connector);
    const publication = substackConfig(connector);
    const athlete = stravaConfig(connector);
    setForm({
      type: connector.type,
      name: connector.name,
      athlete: athlete?.athleteId ?? '',
      handle: config?.handle ?? '',
      channelId: config?.handle ? '' : (config?.channelId ?? ''),
      repository: github ? `${github.owner}/${github.repo}` : '',
      url: publication?.hostname
        ? `https://${publication.hostname}`
        : (publication?.subdomain ?? ''),
    });
    setEditingId(connector.id);
    setView('form');
    setErrorMessage(null);
  };

  const startViewRuns = (connector: Connector) => {
    setRunsConnectorId(connector.id);
    setView('runs');
    setEditingId(null);
    setErrorMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const config = buildConfig(form);
    if (!config) {
      setErrorMessage(t(missingConfigMessage(form.type)));
      return;
    }

    setErrorMessage(null);
    const name = form.name.trim();
    if (editingId) {
      const result = await patchConnector({
        id: editingId,
        ...(name ? { name } : {}),
        config,
      });
      const error = getErrorMessage(result, t('invalidSubmission'));
      if (error) {
        setErrorMessage(error);
        return;
      }
    } else {
      const result = await addConnector({
        type: form.type,
        ...(name ? { name } : {}),
        config,
      });
      const error = getErrorMessage(result, t('invalidSubmission'));
      if (error) {
        setErrorMessage(error);
        return;
      }
    }
    resetForm();
  };

  const handleRun = async (connectorId: string) => {
    if (isBusy || isBillingLoading) {
      return;
    }
    if (!hasBillingSetup) {
      setErrorMessage(t('connectorBillingRequired'));
      return;
    }
    setErrorMessage(null);
    const result = await runConnector(connectorId);
    const error = getErrorMessage(result, t('invalidSubmission'));
    if (error) {
      setErrorMessage(error);
    }
  };

  const handleCancelRun = async (runId: string) => {
    if (!runsConnectorId || isCancelling) {
      return;
    }
    setErrorMessage(null);
    const result = await cancelConnectorRun({
      connectorId: runsConnectorId,
      runId,
    });
    const error = getErrorMessage(result, t('invalidSubmission'));
    if (error) {
      setErrorMessage(error);
    }
  };

  const handleDelete = async (connectorId: string) => {
    setErrorMessage(null);
    const result = await deleteConnector(connectorId);
    const error = getErrorMessage(result, t('invalidSubmission'));
    if (error) {
      setErrorMessage(error);
    }
    if (editingId === connectorId) {
      resetForm();
    }
  };

  return (
    <>
      <div className={styles.header}>
        <h5 className={styles.title}>
          {view === 'runs' && runsConnector ? runsConnector.name : t('connectors')}
        </h5>
        {
          view === 'list' && hasBillingSetup && availableCatalog.length > 0 && (
            <Button
              onClick={startCreate}
              variant="primary"
            >
              {t('addConnector')}
            </Button>
          )
        }
        {
          (view === 'catalog' || view === 'runs') && (
            <Button
              onClick={resetForm}
              type="button"
              variant="secondary"
            >
              {t('cancel')}
            </Button>
          )
        }
      </div>
      {
        view === 'list' && !isBillingLoading && !hasBillingSetup && (
          <Alert variant="warning">
            {t('connectorBillingRequired')}
            {' '}
            <Link to={links.settings('billing')}>{t('setUpBilling')}</Link>
          </Alert>
        )
      }
      {
        (errorMessage || connectorsLoadError) && (
          <Alert variant="danger">
            {errorMessage || connectorsLoadError}
          </Alert>
        )
      }
      {
        view === 'catalog' && (
          <RBForm.Control
            aria-label={t('searchConnectors')}
            autoComplete="off"
            className={styles.search}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder={t('searchConnectors')}
            type="text"
            value={searchQuery}
          />
        )
      }
      {
        view === 'list' && !connectorsFailed && (
          connectorsList.length === 0 ? (
            <p>{t('noConnectorsToDisplay')}</p>
          ) : (
            <table className={styles.list}>
              <tbody>
                {
                  connectorsList.map(connector => (
                    <tr
                      className={styles.row}
                      key={connector.id}
                    >
                      <td>
                        <button
                          className={styles.rowName}
                          onClick={() => startViewRuns(connector)}
                          type="button"
                        >
                          <div>{connector.name}</div>
                          <div className={styles.meta}>
                            {t(`connectorType_${connector.type}`, { defaultValue: connector.type })}
                            {connectorIdentifier(connector) && ` · ${connectorIdentifier(connector)}`}
                            {
                              youtubeMetadata(connector)?.title
                                && youtubeMetadata(connector)?.title !== connector.name
                                && ` · ${youtubeMetadata(connector)?.title}`
                            }
                            {stravaAthleteName(connector) && ` · ${stravaAthleteName(connector)}`}
                            {connectorStatsLabel(connector, t) && ` · ${connectorStatsLabel(connector, t)}`}
                          </div>
                        </button>
                      </td>
                      <td className={styles.actions}>
                        <span title={t('runConnector')}>
                          <Icon
                            name="caret-right"
                            onClick={() => handleRun(connector.id)}
                            style={{
                              cursor: isBusy ? 'default' : 'pointer',
                              marginRight: 8,
                              width: 18,
                            }}
                          />
                        </span>
                        <Icon
                          name="clock"
                          onClick={() => startViewRuns(connector)}
                          style={{
                            cursor: 'pointer',
                            marginRight: 8,
                            width: 18,
                          }}
                        />
                        <Icon
                          name="edit"
                          onClick={() => startEdit(connector)}
                          style={{
                            cursor: 'pointer',
                            marginRight: 8,
                            width: 18,
                          }}
                        />
                        <TrashIcon
                          onClick={() => handleDelete(connector.id)}
                          style={{
                            color: colors.danger,
                            cursor: isBusy ? 'default' : 'pointer',
                            width: 18,
                          }}
                        />
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          )
        )
      }
      {
        view === 'runs' && (
          isRunsLoading ? (
            <Spinner
              animation="border"
              aria-label={t('loading')}
              role="status"
              size="sm"
            />
          ) : runsList.length === 0 ? (
            <p>{t('noConnectorRunsToDisplay')}</p>
          ) : (
            <table className={styles.list}>
              <tbody>
                {
                  runsList.map((run: ConnectorRun) => (
                    <tr
                      className={styles.row}
                      key={run.id}
                    >
                      <td>
                        <div className={styles.runHeader}>
                          <div
                            className={
                              run.status === 'error'
                                ? styles.statusError
                                : run.status === 'cancelled'
                                  ? styles.statusCancelled
                                  : styles.statusSuccess
                            }
                          >
                            {t(`connectorRunStatus_${run.status}`, { defaultValue: run.status })}
                          </div>
                          {
                            run.status === 'queued' && (
                              <Button
                                disabled={isCancelling}
                                onClick={() => handleCancelRun(run.id)}
                                size="sm"
                                type="button"
                                variant="secondary"
                              >
                                {t('cancelConnectorRun')}
                              </Button>
                            )
                          }
                        </div>
                        <div className={styles.meta}>
                          {formatRunTime(run.createdAt)}
                          {
                            run.status !== 'queued' && run.status !== 'cancelled' && (
                              ` · ${formatDurationMs(run.durationMs)}`
                            )
                          }
                          {
                            run.status !== 'queued' && run.status !== 'cancelled' && (
                              ` · ${
                                run.billedUnits > 0
                                  ? t('connectorRunCharge', { amount: connectorRunChargeAmount(run.billedUnits) })
                                  : t('connectorRunBilledUnits', { count: run.billedUnits })
                              }`
                            )
                          }
                        </div>
                        {
                          run.error && (
                            <div className={styles.meta}>{run.error}</div>
                          )
                        }
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          )
        )
      }
      {
        view === 'catalog' && (
          filteredCatalog.length > 0 ? (
            <>
              <p className={styles.meta}>{t('chooseConnectorType')}</p>
              <div className={styles.tiles}>
                {
                  filteredCatalog.map(item => (
                    <button
                      className={styles.tile}
                      key={item.type}
                      onClick={() => startCreateType(item.type)}
                      style={{
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #d0d7de',
                        borderRadius: 8,
                        color: '#24292f',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        minHeight: 148,
                        padding: '20px 12px 16px',
                        width: 168,
                      }}
                      type="button"
                    >
                      <ConnectorLogo
                        className={styles.tileLogo}
                        type={item.type}
                      />
                      <span className={styles.tileLabel}>
                        {item.label || t(`connectorType_${item.type}`)}
                      </span>
                      <span className={styles.tileDescription}>
                        {t(`connectorDescription_${item.type}`, {
                          defaultValue: item.description,
                        })}
                      </span>
                    </button>
                  ))
                }
              </div>
            </>
          ) : (
            <p>{t('noConnectorTypesMatchSearch')}</p>
          )
        )
      }
      {
        view === 'form' && (
          <form
            className={styles.form}
            onSubmit={handleSubmit}
          >
            <div className="mb-3">
              <label className="form-label">{t('type')}</label>
              <div>{t(`connectorType_${form.type}`, { defaultValue: form.type })}</div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t('name')}</label>
              <RBForm.Control
                onChange={event => setForm({ ...form, name: event.target.value })}
                value={form.name}
              />
            </div>
            {
              form.type === 'GITHUB' ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">{t('githubRepository')}</label>
                    <RBForm.Control
                      onChange={event => setForm({ ...form, repository: event.target.value })}
                      placeholder="owner/repo"
                      value={form.repository}
                    />
                  </div>
                  <p className={styles.meta}>{t('provideGitHubRepository')}</p>
                </>
              ) : form.type === 'STRAVA' ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">{t('stravaAthlete')}</label>
                    <RBForm.Control
                      onChange={event => setForm({ ...form, athlete: event.target.value })}
                      placeholder="12345678"
                      value={form.athlete}
                    />
                  </div>
                  <p className={styles.meta}>{t('provideStravaAthlete')}</p>
                </>
              ) : form.type === 'SUBSTACK' ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">{t('substackUrl')}</label>
                    <RBForm.Control
                      onChange={event => setForm({ ...form, url: event.target.value })}
                      placeholder="https://example.substack.com"
                      value={form.url}
                    />
                  </div>
                  <p className={styles.meta}>{t('provideSubstackUrl')}</p>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <label className="form-label">{t('youtubeHandle')}</label>
                    <RBForm.Control
                      onChange={event => setForm({
                        ...form,
                        handle: event.target.value,
                        ...(event.target.value.trim() ? { channelId: '' } : {}),
                      })}
                      placeholder="@channel"
                      value={form.handle}
                    />
                  </div>
                  <div className={styles.or}>{t('or')}</div>
                  <div className="mb-3">
                    <label className="form-label">{t('youtubeChannelId')}</label>
                    <RBForm.Control
                      onChange={event => setForm({
                        ...form,
                        channelId: event.target.value,
                        ...(event.target.value.trim() ? { handle: '' } : {}),
                      })}
                      value={form.channelId}
                    />
                  </div>
                  <p className={styles.meta}>{t('provideHandleOrChannelId')}</p>
                </>
              )
            }
            <Button
              className="me-2"
              disabled={isBusy}
              type="submit"
              variant="success"
            >
              {t('save')}
            </Button>
            <Button
              disabled={isBusy}
              onClick={cancelForm}
              type="button"
              variant="secondary"
            >
              {t('cancel')}
            </Button>
          </form>
        )
      }
    </>
  );
};

export default SettingsConnectors;
